import { isTimeBefore6PM, isWeekday } from "../../Utils/utils";
import { isPrimaryCustomerGroup } from "../../Utils/liveGuestCheckinTag";

/**
 * Pure resolution of member rate dropdown / booking flags (no React state).
 * Mirrors CheckoutPage getMemberRateSelectedOption logic.
 *
 * @param {Object|null|undefined} override - memberRateByBookingItemId[id]: null = user cleared, object = selection, undefined = use booking
 * @param {Object} booking
 * @returns {{ item: { id: number, title: string } }|undefined}
 */
export function resolveMemberRateOption(override, booking) {
	if (override === null) return undefined;
	if (override !== undefined) return { item: override };
	if (booking?.isMemberRateApplied === 1 || booking?.isMemberRateApplied === true) {
		return { item: { id: 2, title: "Member Rate (OP)" } };
	}
	if (
		(booking?.isMemberRateApplied === 0 || booking?.isMemberRateApplied === false) &&
		(booking?.isMembershipApplied === 1 || booking?.isMembershipApplied === true)
	) {
		return { item: { id: 1, title: "Member Rate" } };
	}
	return undefined;
}

/**
 * Same ordering as CheckoutPage: primary customer block(s) first, then guests.
 * @param {Record<string, Array>} transformedCustomerBookingItems - output of transformCustomerData
 * @param {string|undefined} primaryUserName - bookingDetails.user.name
 * @returns {Array<Object>} Flat list of booking rows in UI order (Service A first).
 */
export function flattenBookingsInCheckoutOrder(transformedCustomerBookingItems, primaryUserName) {
	if (!transformedCustomerBookingItems || typeof transformedCustomerBookingItems !== "object") {
		return [];
	}
	const primaryCustomerData = [];
	const guestData = [];
	Object.entries(transformedCustomerBookingItems).forEach(([customerName, bookings]) => {
		const bookingList = Array.isArray(bookings) ? bookings : [];
		if (isPrimaryCustomerGroup(customerName, bookingList, primaryUserName)) {
			primaryCustomerData.push({ customerName, bookings: bookingList });
		} else {
			guestData.push({ customerName, bookings: bookingList });
		}
	});
	const ordered = [...primaryCustomerData, ...guestData];
	return ordered.flatMap(({ bookings }) => bookings);
}

/**
 * Flags for booking_item from resolved member rate (id 1 = weekday/evening, id 2 = OP).
 */
export function flagsFromMemberRateOption(option) {
	const id = option?.item?.id;
	if (id === 2) return { isMembershipApplied: 1, isMemberRateApplied: 1 };
	if (id === 1) return { isMembershipApplied: 1, isMemberRateApplied: 0 };
	return { isMembershipApplied: 0, isMemberRateApplied: 0 };
}

/**
 * Default OP vs standard member rate for anchor when no explicit option (follow first service's time slot).
 */
export function computeMemberRateAppliedFromSchedule({
	serviceTime,
	serviceDate,
	publicHoliday,
}) {
	const time = serviceTime;
	if (publicHoliday || !isTimeBefore6PM(time) || !isWeekday(serviceDate)) return 0;
	return 1;
}

/**
 * When global "Apply membership discount" is on, for each member (customer / pax lane) the **first
 * service** in that lane sets Member Rate vs Member Rate (OP) from schedule rules only (weekday,
 * before/after 6pm, holiday). All other services in that lane match the first. Per-row dropdown
 * overrides are ignored for that computation so activation always follows conditions.
 * When off, clear member rate everywhere and clear overrides map.
 * When on, each booking row also has `discount_option` cleared so discounts do not fall back from API
 * (mutual exclusivity with global membership).
 *
 * @param {Object} params
 * @param {Record<string, Array>|null} params.rawCustomerBookingItems
 * @param {Record<string, Array>} params.transformedForOrder - transformCustomerData(prev); kept for API compatibility
 * @param {string|undefined} params.primaryUserName
 * @param {Record<number, Object|null|undefined>} params.memberRateOverrides - ignored when applying (schedule + first service per member wins)
 * @param {boolean} params.applyMembershipDiscount
 * @param {string|undefined} params.serviceDate - booking service date
 * @param {boolean} params.publicHoliday
 * @param {boolean} params.isPremiumOutlet
 * @returns {{ nextItems: typeof rawCustomerBookingItems, nextMemberOverrides: Record<number, Object> }}
 */
export function applyGlobalMembershipDiscountToBookings({
	rawCustomerBookingItems,
	transformedForOrder: _transformedForOrder,
	primaryUserName: _primaryUserName,
	memberRateOverrides: _memberRateOverrides,
	applyMembershipDiscount,
	serviceDate,
	publicHoliday,
	isPremiumOutlet = false,
}) {
	if (!rawCustomerBookingItems || typeof rawCustomerBookingItems !== "object") {
		return { nextItems: rawCustomerBookingItems, nextMemberOverrides: {} };
	}

	if (!applyMembershipDiscount) {
		const cleared = {};
		for (const [customerName, bookings] of Object.entries(rawCustomerBookingItems)) {
			const list = Array.isArray(bookings) ? bookings : [];
			cleared[customerName] = list.map((booking) => ({
				...booking,
				isMembershipApplied: 0,
				isMemberRateApplied: 0,
			}));
		}
		return { nextItems: cleared, nextMemberOverrides: {} };
	}

	const nextItems = {};
	const nextMemberOverrides = {};

	for (const [customerName, bookings] of Object.entries(rawCustomerBookingItems)) {
		const list = Array.isArray(bookings) ? bookings : [];
		if (list.length === 0) {
			nextItems[customerName] = [];
			continue;
		}

		const first = list[0];
		const isMemberRateApplied = isPremiumOutlet
			? 0
			: computeMemberRateAppliedFromSchedule({
				serviceTime: first?.start_time || first?.service_time,
				serviceDate,
				publicHoliday,
			});
		const flags = { isMembershipApplied: 1, isMemberRateApplied };
		const templateId = isMemberRateApplied ? 2 : 1;
		const templateTitle = templateId === 2 ? "Member Rate (OP)" : "Member Rate";

		// Strip per-service discount when global membership applies (mutual exclusivity + global override).
		nextItems[customerName] = list.map((booking) => ({
			...booking,
			...flags,
			isDiscountApplied: false,
			discount_option: null,
			price_adjustment: null,
		}));

		for (const booking of list) {
			const id = booking?.id;
			if (id == null) continue;
			nextMemberOverrides[id] = {
				id: templateId,
				title: templateTitle,
				bookingItemId: id,
			};
		}
	}

	return { nextItems, nextMemberOverrides };
}
